import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import Swal from "sweetalert2";
import {DataService} from "../../../services/data.service";
import {AccessService} from "../../../services/access.service";
import {ProjectService} from "../../../services/project.service";
import {ToastrService} from "ngx-toastr";
import {LightStatus, LightTab, ProjectStatus, TeamRole, ViewMode, strategicClassifications} from "../../interfaces";
import {UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Observable, Observer, of, Subscription, switchMap} from "rxjs";
import {TypeaheadMatch, TypeaheadModule} from "ngx-bootstrap/typeahead";
import {map} from "rxjs/operators";
import {NgIf, NgFor, DecimalPipe, DatePipe} from '@angular/common';
import {FormService} from "../../../services/form.service";

@Component({
    selector: 'app-light-detail',
    templateUrl: './light-detail.component.html',
    styleUrl: './light-detail.component.scss',
    imports: [NgIf, RouterLink, FormsModule, ReactiveFormsModule, TypeaheadModule, NgFor, DecimalPipe, DatePipe]
})
export class LightDetailComponent implements OnInit, OnDestroy {

    accessService = inject(AccessService);
    projectService = inject(ProjectService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private dataService = inject(DataService);
    private toastrService = inject(ToastrService);
    private fb = inject(UntypedFormBuilder);
    private formService = inject(FormService);

    readonly ProjectStatus = ProjectStatus;
    readonly TeamRole = TeamRole;
    readonly strategicClassifications = strategicClassifications;
    readonly LightStatus = LightStatus;
    readonly LightTab = LightTab;
    readonly ViewMode = ViewMode;

    PID: string;
    project: any;

    viewMode!: ViewMode;
    tab: LightTab = LightTab.GENERAL;
    lightStatusList = Object.values(LightStatus);

    countries: any[];
    locations: any[] = [];
    private countryChanges$: Subscription;
    private strategicClassificationChanges$: Subscription;

    form!: UntypedFormGroup;

    // Set to true when access to the project is denied as per user settings
    accessDenied = false;

    // Set to true when project does not exist
    missing = false;

    exporting = false;

    regions: any[] = [];
    departments: any[] = [];

    newStatusReport: any = null;
    statusReports: any[] = [];

    users$!: Observable<any[]>;
    noUserResult = false;
    loading = false;

    ngOnInit(): void {
        this.PID = this.route.snapshot.paramMap.get('id');

        this.viewMode = this.PID == '0' ? ViewMode.INSERT : ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            countries: [null],
            id_location: [''],
            lead: ['', Validators.required],
            title: ['', Validators.required],
            benefit: [''],
            status: [LightStatus.OPEN, Validators.required],
            budget: [''],
            start: ['', Validators.required],
            end: [''],

            strategic_classification_root: [''],
            strategic_classification: [''],

            id_department: [''],
            id_region: [''],

            department: [''],
            region: [''],
            countryNames: [''],
            location: [''],

            _userSearch: ['', Validators.required],
        });

        this.countryChanges$ = this.form.get('countries').valueChanges.subscribe(val => {
            this.form.get('id_location').patchValue('');

            // if only 1 country is selected load locations
            if (val.length === 1) {
                this.locations = val[0].locations;
            }
        });

        this.strategicClassificationChanges$ = this.form.get('strategic_classification_root').valueChanges.subscribe(val => {
            this.formService.addValidators(this.form, ['strategic_classification'], [Validators.required]);
        });

        if (this.PID !== '0') {
            this.getProject();
        }

        // search users
        this.users$ = new Observable((observer: Observer<string | undefined>) => {
            observer.next(this.form.get('_userSearch').value);
        }).pipe(
            switchMap((query: string) => {
                if (query) {
                    this.noUserResult = false;

                    return this.dataService.request('User/listAzure', {
                        search: query
                    }).pipe(
                        map((data: any) => data.users)
                    );
                }

                return of([]);
            })
        );

        this.dataService.request("Country/listWithLocations").subscribe(response => {
            this.countries = response.countries;

            // move first entry (-Gruppe-) to the end
            this.countries.push(this.countries.shift());

            this.setCountries();
        });

        this.dataService.request("Region/listForProject").subscribe(response => {
            this.regions = response.regions;
        });

        this.dataService.request("Department/listForProject").subscribe(response => {
            this.departments = response.departments;
        });
    }

    ngOnDestroy(): void {
        this.countryChanges$?.unsubscribe();
        this.strategicClassificationChanges$?.unsubscribe();
    }

    getProject(): void {
        this.dataService.request("Light/get", {
            PID: this.PID
        }).subscribe(response => {
            this.project = response.project;

            this.patchForm();
        })

        this.getStatusReports();
    }

    getStatusReports(): void {
        this.dataService.request("Light/getStatusReports", {
            PID: this.PID
        }).subscribe(response => {
            this.statusReports = response.reports;
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.project.id,
            //countries: this.project.countries, // this is done in setCountries()
            id_location: this.project.id_location ?? '',
            lead: this.project.lead,
            status: this.project.status,
            title: this.project.title,
            benefit: this.project.benefit,
            id_region: this.project.id_region ?? '',
            id_department: this.project.id_department ?? '',
            budget: this.project.budget,
            start: this.project.start,
            end: this.project.end,

            strategic_classification_root: this.project.strategic_classification ? strategicClassifications.find(sc => sc.subs.includes(this.project.strategic_classification)) : '',
            strategic_classification: this.project.strategic_classification ?? '',

            department: this.project.department,
            region: this.project.region,
            countryNames: this.project.countries.map(country => country.name).join(', '), // for display only
            location: this.project.location,

            _userSearch: this.project.lead,
        });
    }

    // only display the location if there is only one country selected, and it is not the group
    displayLocation(): boolean {
        if (!this.form.value.countries) return false;
        if (this.form.value.countries.length != 1) return false;

        return this.form.value.countries[0].name !== '-Gruppe-'
    }

    setCountries(): void {
        if (!this.countries || !this.project) {
            return;
        }

        // set country
        this.form.patchValue({
            countries: this.countries.filter(country => {
                return this.project.countries.map(country => country.id_country).includes(country.id);
            }),
        });
    }

    gotoList(): void {
        this.router.navigate(['/light']);
    }

    setTab(tab: LightTab): void {
        this.tab = tab;
    }

    delete(): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Das Projekt wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Light/delete', {PID: this.PID}).subscribe(() => {
                    this.router.navigate(['/light']);
                });
            }
        });
    }

    save(): void {
        this.dataService.request('Light/save', {
            project: this.form.getRawValue(),
        }).subscribe(response => {
            if (response.errors.length == 0) {
                this.toastrService.success('Erfolgreich gespeichert')
                this.router.navigate(['/light']);
            }
        });
    }

    update(): void {
        this.dataService.request('Light/update', {
            project: this.form.getRawValue(),
        }).subscribe(() => {
            this.toastrService.success('Erfolgreich gespeichert')
            this.setMode(ViewMode.VIEW);
            this.getProject();
        });
    }

    cancelCreate(): void {
        this.router.navigate(['/light']);
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
    }

    setReportStatus(entry, status) {
        entry.color = status;
    }

    addStatusReport() {
        this.newStatusReport = {
            id: null,
            id_light: this.PID,
            color: 'Grün',
            comment: '',
            created_at: new Date(),
        };
    }

    cancelAddStatusReport() {
        this.newStatusReport = null;
    }

    saveStatusReport() {
        this.dataService.request('Light/saveStatusReport', {
            report: this.newStatusReport,
        }).subscribe(() => {
            this.toastrService.success('Erfolgreich gespeichert')
            this.newStatusReport = null;
            this.getStatusReports();
        });
    }

    editStatusReport(entry: any) {
        entry.editing = true;
    }

    cancelEditStatusReport(entry: any) {
        entry.editing = false;
    }

    updateStatusReport(entry: any) {
        this.dataService.request('Light/updateStatusReport', {
            report: entry,
        }).subscribe(() => {
            this.toastrService.success('Erfolgreich aktualisiert')
            entry.editing = false;
            this.getStatusReports();
        });
    }

    deleteStatusReport(entry: any) {
        Swal.fire({
            title: 'Sicher?',
            text: 'Der Statuseintrag wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('Light/deleteStatusReport', {
                    report: entry,
                }).subscribe(() => {
                    this.toastrService.success('Erfolgreich gelöscht')
                    this.getStatusReports();
                });
            }
        });
    }

    onSelectUser($event: TypeaheadMatch): void {
        console.log($event.item);
        this.form.patchValue({
            lead: $event.item.displayName
        });
    }

    typeaheadUsersNoResults($event: boolean): void {
        this.noUserResult = $event;
    }

    typeaheadUsersLoading($event: boolean): void {
        this.loading = $event;
    }
}
